import React, { useEffect, useState, useCallback } from "react";
import {
  CalendarDays,
  Edit3,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Trash2,
  Users,
  X,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const COVER_PRESETS = ["#0B1E36", "#13294B", "#1F3A5F", "#1A1A1A"];

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div
      data-testid="admin-plans-toast"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 text-xs uppercase tracking-[0.2em] border ${
        msg.ok
          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : "bg-rose-50 border-rose-300 text-rose-800"
      }`}
    >
      {msg.text}
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // { mode: "create" | "edit", plan }
  const [daysModal, setDaysModal] = useState(null); // { plan }
  const [subscribersModal, setSubscribersModal] = useState(null); // { plan }
  const [running, setRunning] = useState(false);

  const showToast = (text, ok = true, ttl = 3000) => {
    setToast({ text, ok });
    setTimeout(() => setToast(null), ttl);
  };

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/plans")
      .then(({ data }) => setPlans(data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/plans/${p.slug}`, { published: !p.published });
      load();
    } catch (err) {
      showToast("Não foi possível alterar.", false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir o plano "${p.title}" e todos os dias?`)) return;
    try {
      await api.delete(`/admin/plans/${p.slug}`);
      showToast("Plano excluído.");
      load();
    } catch (err) {
      showToast("Não foi possível excluir.", false);
    }
  };

  const runCron = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/plans/run-cron");
      showToast(
        `Cron executado: ${data.dispatched} despacho(s), ${data.completed} concluído(s).`
      );
    } catch (err) {
      showToast("Falha ao rodar o cron.", false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      data-testid="admin-plans-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <div className="flex items-center gap-3 text-[#C5A059]">
          <CalendarDays size={16} strokeWidth={1.5} />
          <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
            Planos de Estudo
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={runCron}
            disabled={running}
            data-testid="admin-plans-run-cron"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] border border-[#0B1E36]/15 px-3 py-2 hover:bg-[#FAF8F4] disabled:opacity-50"
            title="Roda manualmente o ciclo diário de envios (mock)"
          >
            {running ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Zap size={12} strokeWidth={1.6} />
            )}
            Rodar cron agora
          </button>
          <button
            onClick={load}
            data-testid="admin-plans-refresh"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059]"
          >
            <RefreshCw size={12} strokeWidth={1.5} />
            Atualizar
          </button>
          <button
            onClick={() =>
              setEditing({ mode: "create", plan: null })
            }
            data-testid="admin-plans-create"
            className="inline-flex items-center gap-2 bg-[#0B1E36] text-white px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#13294B]"
          >
            <Plus size={12} strokeWidth={1.6} />
            Novo plano
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-[#0B1E36]/55 leading-[1.6] mb-6">
        Cada plano tem um cronograma de dias (versículo + reflexão + aplicação).
        Quem se inscreve recebe o Dia 1 imediatamente; o cron diário (08h
        Brasília) avança para o próximo dia. Hoje os e-mails ficam{" "}
        <strong>simulados</strong> em <code>plan_dispatches</code>: assim que o
        Resend for ligado, vira envio real.
      </p>

      {loading ? (
        <div className="py-10 text-center text-sm text-[#0B1E36]/40">
          Carregando…
        </div>
      ) : plans.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#0B1E36]/50 border border-dashed border-[#0B1E36]/15">
          Nenhum plano cadastrado ainda.
        </div>
      ) : (
        <ul
          data-testid="admin-plans-list"
          className="divide-y divide-[#0B1E36]/8 border-y border-[#0B1E36]/8"
        >
          {plans.map((p) => (
            <li
              key={p.id}
              data-testid={`admin-plans-row-${p.slug}`}
              className="py-4 grid grid-cols-12 gap-4 items-center"
            >
              <div className="col-span-12 md:col-span-5">
                <Link
                  to={`/planos/${p.slug}`}
                  className="text-sm text-[#0B1E36] font-medium hover:text-[#C5A059]"
                >
                  {p.title}
                </Link>
                {p.subtitle && (
                  <div className="text-xs text-[#0B1E36]/55 truncate">
                    {p.subtitle}
                  </div>
                )}
                {!p.published && (
                  <span className="mt-1 inline-block text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/40 border border-[#0B1E36]/20 px-2 py-0.5">
                    Despublicado
                  </span>
                )}
              </div>
              <div className="col-span-4 md:col-span-2 text-xs text-[#0B1E36]/60">
                {p.duration_days} dias
                <div className="text-[0.65rem] text-[#0B1E36]/40 mt-0.5">
                  {p.day_count || 0} cadastrado(s)
                </div>
              </div>
              <div className="col-span-4 md:col-span-2 text-xs text-[#0B1E36]/60">
                <span className="inline-flex items-center gap-1">
                  <Users size={11} strokeWidth={1.6} />
                  {p.subscriber_count || 0}
                </span>
              </div>
              <div className="col-span-12 md:col-span-3 flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => setDaysModal({ plan: p })}
                  data-testid={`admin-plans-days-${p.slug}`}
                  className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] border border-[#0B1E36]/15 px-2 py-1"
                >
                  Dias
                </button>
                <button
                  onClick={() => setSubscribersModal({ plan: p })}
                  data-testid={`admin-plans-subs-${p.slug}`}
                  className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] border border-[#0B1E36]/15 px-2 py-1"
                >
                  Inscritos
                </button>
                <button
                  onClick={() => togglePublish(p)}
                  data-testid={`admin-plans-toggle-${p.slug}`}
                  className="text-[#0B1E36]/60 hover:text-[#C5A059]"
                  title={p.published ? "Despublicar" : "Publicar"}
                >
                  {p.published ? (
                    <Eye size={14} strokeWidth={1.6} />
                  ) : (
                    <EyeOff size={14} strokeWidth={1.6} />
                  )}
                </button>
                <button
                  onClick={() => setEditing({ mode: "edit", plan: p })}
                  data-testid={`admin-plans-edit-${p.slug}`}
                  className="text-[#0B1E36]/60 hover:text-[#C5A059]"
                >
                  <Edit3 size={14} strokeWidth={1.6} />
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`admin-plans-delete-${p.slug}`}
                  className="text-rose-700 hover:text-rose-900"
                >
                  <Trash2 size={14} strokeWidth={1.6} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PlanFormModal
          mode={editing.mode}
          plan={editing.plan}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            showToast(msg);
            setEditing(null);
            load();
          }}
        />
      )}
      {daysModal && (
        <PlanDaysModal
          plan={daysModal.plan}
          onClose={() => setDaysModal(null)}
          onSaved={(msg) => {
            showToast(msg);
            setDaysModal(null);
            load();
          }}
        />
      )}
      {subscribersModal && (
        <PlanSubscribersModal
          plan={subscribersModal.plan}
          onClose={() => setSubscribersModal(null)}
        />
      )}

      <Toast msg={toast} />
    </section>
  );
}

function PlanFormModal({ mode, plan, onClose, onSaved }) {
  const [form, setForm] = useState(
    plan
      ? { ...plan }
      : {
          title: "",
          subtitle: "",
          description: "",
          duration_days: 7,
          category: "Família",
          cover_color: "#0B1E36",
          published: true,
          plan_order: 1,
        }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      if (mode === "create") {
        await api.post("/admin/plans", form);
        onSaved("Plano criado.");
      } else {
        await api.put(`/admin/plans/${plan.slug}`, form);
        onSaved("Plano atualizado.");
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div
        data-testid="admin-plans-modal"
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-[#0B1E36]/10 px-6 py-4">
          <h3 className="text-[0.72rem] uppercase tracking-[0.28em] text-[#0B1E36]/70">
            {mode === "create" ? "Novo plano" : `Editar: ${plan.title}`}
          </h3>
          <button onClick={onClose} className="text-[#0B1E36]/50 hover:text-[#0B1E36]">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-6 space-y-4">
          <div>
            <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
              Título
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="admin-plans-form-title"
              className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
              Subtítulo
            </label>
            <input
              type="text"
              value={form.subtitle || ""}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
              Descrição
            </label>
            <textarea
              rows={4}
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Dias
              </label>
              <input
                type="number"
                min={1}
                max={365}
                value={form.duration_days}
                onChange={(e) =>
                  setForm({ ...form, duration_days: Number(e.target.value) })
                }
                className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm tabular-nums"
              />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Ordem
              </label>
              <input
                type="number"
                min={0}
                value={form.plan_order}
                onChange={(e) =>
                  setForm({ ...form, plan_order: Number(e.target.value) })
                }
                className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm tabular-nums"
              />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
                Categoria
              </label>
              <input
                type="text"
                value={form.category || ""}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
                className="mt-1 w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
              Cor da capa
            </label>
            {COVER_PRESETS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setForm({ ...form, cover_color: c })}
                className={`w-7 h-7 rounded-full border-2 ${
                  form.cover_color === c
                    ? "border-[#C5A059]"
                    : "border-transparent"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-[#0B1E36]/70">
            <input
              type="checkbox"
              checked={!!form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            Publicado
          </label>

          {err && (
            <div className="text-xs px-3 py-2 border border-rose-300 bg-rose-50 text-rose-800">
              {err}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 px-3 py-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !form.title}
              data-testid="admin-plans-form-submit"
              className="bg-[#0B1E36] text-white px-5 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#13294B] disabled:opacity-50"
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlanDaysModal({ plan, onClose, onSaved }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/plans/${plan.slug}/days`)
      .then(({ data }) => {
        const list = (data || []).slice().sort(
          (a, b) => a.day_number - b.day_number
        );
        // pad to duration_days
        while (list.length < plan.duration_days) {
          list.push({
            day_number: list.length + 1,
            title: "",
            verse_reference: "",
            verse_text: "",
            reflection: "",
            sermon_slug: "",
            sermon_title: "",
            application: "",
          });
        }
        setDays(list);
      })
      .finally(() => setLoading(false));
  }, [plan]);

  const setField = (idx, field, value) =>
    setDays((arr) =>
      arr.map((d, i) => (i === idx ? { ...d, [field]: value } : d))
    );

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      // Sanitize: remove empty rows at the end
      const payload = days
        .filter((d) => d.title && d.title.trim().length > 0)
        .map((d) => ({
          day_number: d.day_number,
          title: d.title,
          verse_reference: d.verse_reference || null,
          verse_text: d.verse_text || null,
          reflection: d.reflection || "",
          sermon_slug: d.sermon_slug || null,
          sermon_title: d.sermon_title || null,
          application: d.application || null,
        }));
      await api.put(`/admin/plans/${plan.slug}/days`, { days: payload });
      onSaved(`${payload.length} dia(s) salvo(s).`);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Falha ao salvar dias.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div
        data-testid="admin-plans-days-modal"
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-[#0B1E36]/10 px-6 py-4 sticky top-0 bg-white z-10">
          <h3 className="text-[0.72rem] uppercase tracking-[0.28em] text-[#0B1E36]/70">
            Dias do plano: {plan.title}
          </h3>
          <button onClick={onClose} className="text-[#0B1E36]/50 hover:text-[#0B1E36]">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-6">
          {loading ? (
            <div className="text-center text-sm text-[#0B1E36]/50 py-10">
              Carregando…
            </div>
          ) : (
            <div className="space-y-6">
              {days.map((d, i) => (
                <div
                  key={d.day_number}
                  className="border border-[#0B1E36]/10 p-4"
                  data-testid={`admin-plans-day-row-${d.day_number}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-serif-display text-xl text-[#C5A059] tabular-nums">
                      {String(d.day_number).padStart(2, "0")}
                    </span>
                    <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36]/50">
                      Dia {d.day_number}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Título do dia"
                    value={d.title || ""}
                    onChange={(e) => setField(i, "title", e.target.value)}
                    className="w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
                  />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <input
                      type="text"
                      placeholder="Versículo (ex: João 3:16)"
                      value={d.verse_reference || ""}
                      onChange={(e) => setField(i, "verse_reference", e.target.value)}
                      className="w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Slug do sermão (opcional)"
                      value={d.sermon_slug || ""}
                      onChange={(e) => setField(i, "sermon_slug", e.target.value)}
                      className="w-full border border-[#0B1E36]/15 py-2 px-3 text-sm"
                    />
                  </div>
                  <textarea
                    rows={2}
                    placeholder="Texto do versículo"
                    value={d.verse_text || ""}
                    onChange={(e) => setField(i, "verse_text", e.target.value)}
                    className="w-full mt-3 border border-[#0B1E36]/15 py-2 px-3 text-sm"
                  />
                  <textarea
                    rows={4}
                    placeholder="Reflexão"
                    value={d.reflection || ""}
                    onChange={(e) => setField(i, "reflection", e.target.value)}
                    className="w-full mt-3 border border-[#0B1E36]/15 py-2 px-3 text-sm"
                  />
                  <textarea
                    rows={2}
                    placeholder="Aplicação prática"
                    value={d.application || ""}
                    onChange={(e) => setField(i, "application", e.target.value)}
                    className="w-full mt-3 border border-[#0B1E36]/15 py-2 px-3 text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          {err && (
            <div className="mt-4 text-xs px-3 py-2 border border-rose-300 bg-rose-50 text-rose-800">
              {err}
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 px-3 py-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="admin-plans-days-save"
              className="bg-[#0B1E36] text-white px-5 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#13294B] disabled:opacity-50"
            >
              {saving ? "Salvando…" : "Salvar dias"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanSubscribersModal({ plan, onClose }) {
  const [data, setData] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/plans/${plan.slug}/subscribers`),
      api.get(`/admin/plans/dispatches?plan_slug=${plan.slug}&limit=50`),
    ])
      .then(([subs, disp]) => {
        setData(subs.data);
        setDispatches(disp.data?.items || []);
      })
      .finally(() => setLoading(false));
  }, [plan]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div
        data-testid="admin-plans-subs-modal"
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-[#0B1E36]/10 px-6 py-4 sticky top-0 bg-white z-10">
          <h3 className="text-[0.72rem] uppercase tracking-[0.28em] text-[#0B1E36]/70">
            Inscritos · {plan.title}
          </h3>
          <button onClick={onClose} className="text-[#0B1E36]/50 hover:text-[#0B1E36]">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-6">
          {loading ? (
            <div className="text-center text-sm text-[#0B1E36]/50 py-10">
              Carregando…
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs text-[#0B1E36]/60 mb-3">
                <Users size={12} strokeWidth={1.6} />
                {data?.total || 0} inscrito(s)
              </div>
              {data?.items?.length ? (
                <ul className="divide-y divide-[#0B1E36]/8 border-y border-[#0B1E36]/8 mb-6">
                  {data.items.map((s) => (
                    <li
                      key={s.id}
                      className="py-3 grid grid-cols-12 gap-2 items-center text-xs"
                    >
                      <div className="col-span-5 truncate text-[#0B1E36]">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-[#0B1E36]/50">{s.email}</div>
                      </div>
                      <div className="col-span-3 text-[#0B1E36]/60">
                        Dia {s.current_day - 1 > 0 ? s.current_day - 1 : "·"}/
                        {plan.duration_days}
                      </div>
                      <div className="col-span-2 text-[#0B1E36]/60">
                        {s.status === "active"
                          ? "ativo"
                          : s.status === "completed"
                          ? "concluído"
                          : s.status}
                      </div>
                      <div className="col-span-2 text-[#0B1E36]/40 text-[0.65rem]">
                        {s.started_at?.slice(0, 10)}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#0B1E36]/50 mb-6">
                  Ninguém inscrito ainda.
                </p>
              )}

              <div className="flex items-center gap-2 text-xs text-[#0B1E36]/60 mb-3">
                <Mail size={12} strokeWidth={1.6} />
                Últimos despachos (mock)
              </div>
              {dispatches.length ? (
                <ul className="space-y-2">
                  {dispatches.map((d) => (
                    <li
                      key={d.id}
                      className="border border-[#0B1E36]/10 p-3 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#0B1E36] font-medium">
                          {d.subject}
                        </span>
                        <span className="text-[#0B1E36]/40 text-[0.65rem]">
                          {d.dispatched_at?.replace("T", " ").slice(0, 16)} ·{" "}
                          {d.simulated ? "MOCK" : "REAL"}
                        </span>
                      </div>
                      <div className="text-[#0B1E36]/60 mt-1">
                        para {d.subscriber_email}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#0B1E36]/50">
                  Nenhum e-mail despachado ainda.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
